import { uniqBy } from 'lodash';
import type { IntlShape } from 'react-intl';

import { Chips } from '~/eds/src/components/Chips/Chips';
import { IconColor } from '~/eds/src/types/IconColor';
import { IconType } from '~/eds/src/types/IconType';
import { TextColor } from '~/eds/src/types/TextColor';
import { TextVariant } from '~/eds/src/types/TextVariant';
import { EntityType } from '~/enums/EntityType';
import {
  getFieldTypeIconProps,
  getFieldTypeLabel,
} from '~/features/fields/utils';
import { mapDataFieldType } from '~/redux/api/methods/sylvanus/transformers';
import { Nullable } from '~/types/Nullable';
import { sortByStringValue } from '~/utils/array';

import { LibraryModelVersion, Tag } from '../types';

export const getEntityTypeIconProps = (
  model: LibraryModelVersion,
  intl: IntlShape,
): { icon: IconType; label: string; tooltip: string } => {
  switch (model.targetEntityDetails?.type) {
    // Extensible as more entity types are supported
    case EntityType.DocumentAttachedData:
      const tableLabel = intl.formatMessage({
        id: 'xRayModelLibraryUtils.TableEntityTypeLabel',
        defaultMessage: 'Table',
        description: 'Label for the table entity type',
      });
      return {
        icon: 'table',
        label: tableLabel,
        tooltip: tableLabel,
      };
    case EntityType.Field:
    default:
      return getFieldTypeIconProps(mapDataFieldType(model.config), intl);
  }
};

export const getEntityTypeLabel = (
  model: LibraryModelVersion,
  intl: IntlShape,
): string => {
  switch (model.targetEntityDetails?.type) {
    // Extensible as more entity types are supported
    case EntityType.DocumentAttachedData:
      return intl.formatMessage({
        id: 'xRayModelLibraryUtils.TableEntityTypeLabel2',
        defaultMessage: 'Table',
        description: 'Label for the table entity type',
      });
    case EntityType.Field:
    default:
      return getFieldTypeLabel(mapDataFieldType(model.config), intl);
  }
};

export const getCategoryChips = ({
  model,
  category,
  mode = 'default',
  intl,
}: {
  model: LibraryModelVersion;
  category: Nullable<Tag>;
  mode?: 'compact' | 'default';
  intl: IntlShape;
}): React.ReactNode => {
  const chips = uniqBy(
    model.tags
      .filter((tag) => category && tag.label !== category.label)
      .map((tag) => ({
        text: tag.label,
      })),
    'text',
  );
  return chips?.length ? (
    <Chips
      chips={chips}
      label={intl.formatMessage({
        id: 'ModelLibraryUtils.categoryChipsLabel',
        defaultMessage: 'Also in',
        description: 'Label for chips showing categories of the model',
      })}
      limit={mode === 'compact' ? 1 : undefined}
      limitMode={mode === 'compact' ? 'readOnly' : undefined}
    />
  ) : undefined;
};

export const getCategoryModels = (
  category: Tag,
  models: LibraryModelVersion[],
): LibraryModelVersion[] => {
  return models.filter((model) =>
    model.tags.some((tag) => tag.label === category.label),
  );
};

export const getSortedCategories = (models: LibraryModelVersion[]): Tag[] => {
  const categories = uniqBy(
    models.flatMap((model) => model.tags),
    'label',
  );
  return categories.sort(sortByStringValue('label'));
};

export const testIsModelPublished = ({
  promptModelPublishStatus,
}: LibraryModelVersion): boolean => {
  return (
    promptModelPublishStatus === 'published' ||
    promptModelPublishStatus === 'error' ||
    promptModelPublishStatus === 'suspended' ||
    promptModelPublishStatus === 'in_progress' ||
    promptModelPublishStatus === 'draft'
  );
};

export const getStatusTextProps = (
  { promptModelPublishStatus }: LibraryModelVersion,
  intl: IntlShape,
) => {
  const base = {
    iconSpacing: 's' as const,
    variant: 'body-medium' as TextVariant,
  };
  const baseInfo = {
    ...base,
    iconColor: 'status.info' as IconColor,
    textColor: 'status.info' as TextColor,
  };
  switch (promptModelPublishStatus) {
    case 'draft':
      return {
        ...baseInfo,
        icon: 'review' as IconType,
        text: intl.formatMessage({
          id: 'xRayModelLibraryUtils.DraftPublishStatus',
          defaultMessage: 'Draft',
          description: 'Label for models in draft state',
        }),
      };
    case 'in_progress':
      return {
        ...baseInfo,
        icon: 'loading-dashed' as IconType,
        text: intl.formatMessage({
          id: 'xRayModelLibraryUtils.PublishingStatus',
          defaultMessage: 'Publishing',
          description: 'Label for models in publishing state',
        }),
      };
    case 'published':
      return {
        ...base,
        icon: 'status-success' as IconType,
        iconColor: 'status.success' as IconColor,
        text: intl.formatMessage({
          id: 'xRayModelLibraryUtils.PublishedStatus',
          defaultMessage: 'Published',
          description: 'Label for models that are published',
        }),
        textColor: 'status.success' as TextColor,
      };
    case 'suspended':
      return {
        ...baseInfo,
        icon: 'pause' as IconType,
        text: intl.formatMessage({
          id: 'xRayModelLibraryUtils.SuspendedStatus',
          defaultMessage: 'Suspended',
          description: 'Label for models in suspended state',
        }),
      };
    case 'error':
    default:
      return undefined;
  }
};
